import React, { useState, useEffect } from "react";
import { Box, Typography, Fade } from "@mui/material";

// Confetti Component
const Confetti = ({ delay, duration, left, color, rotation }) => (
  <Box
    sx={{
      position: "absolute",
      top: "-20px",
      left: `${left}%`,
      width: "10px",
      height: "10px",
      backgroundColor: color,
      opacity: 0.9,
      animation: `confettiFall ${duration}s linear ${delay}s infinite`,
      transform: `rotate(${rotation}deg)`,
      "@keyframes confettiFall": {
        "0%": {
          transform: `translateY(0) rotate(${rotation}deg)`,
          opacity: 1,
        },
        "100%": {
          transform: `translateY(100vh) rotate(${rotation + 720}deg)`,
          opacity: 0,
        },
      },
    }}
  />
);

// Cracker Burst Component with Flash
const CrackerBurst = ({ x, y, delay, duration, onAnimationEnd }) => {
  const colors = ["#FFD700", "#FF1493", "#00CED1", "#FF4500", "#9370DB", "#32CD32"];
  const shapes = ["●", "✦", "★", "✨", "◆", "▲", "■", "✶", "✱", "❋"];

  const particles = Array.from({ length: 20 }, (_, i) => {
    const angle = (i * 360) / 20;
    const angleRad = (angle * Math.PI) / 180;
    const endX = Math.cos(angleRad) * 120;
    const endY = Math.sin(angleRad) * 120;

    return {
      id: i,
      angle,
      endX,
      endY,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
    };
  });

  return (
    <Box
      sx={{
        position: "absolute",
        top: `${y}%`,
        left: `${x}%`,
        width: "2px",
        height: "2px",
      }}
    >
      {/* Flash effect at burst */}
      <Box
        sx={{
          position: "absolute",
          width: "80px",
          height: "80px",
          top: "-40px",
          left: "-40px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 215, 0, 0.3) 30%, transparent 70%)",
          opacity: 0,
          animation: `flashBurst ${duration}s ease-out ${delay}s infinite`,
          "@keyframes flashBurst": {
            "0%, 4.9%": {
              transform: "scale(0)",
              opacity: 0,
              visibility: "hidden",
            },
            "5%": {
              visibility: "visible",
              transform: "scale(1.5)",
              opacity: 0.5,
            },
            "15%": {
              transform: "scale(2.5)",
              opacity: 0,
            },
            "100%": {
              opacity: 0,
              visibility: "hidden",
            },
          },
        }}
        onAnimationIteration={onAnimationEnd}
      />

      {/* Particles - shooting in all directions with variety */}
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            color: particle.color,
            textShadow: `0 0 5px ${particle.color}, 0 0 10px ${particle.color}`,
            opacity: 0,
            animation: `burstParticle ${duration}s ease-out ${delay}s infinite`,
            "--endX": `${particle.endX}px`,
            "--endY": `${particle.endY}px`,
            "@keyframes burstParticle": {
              "0%, 4.9%": {
                transform: "translate(0px, 0px) scale(0) rotate(0deg)",
                opacity: 0,
                visibility: "hidden",
              },
              "5%": {
                visibility: "visible",
                opacity: 0,
              },
              "10%": {
                transform: `translate(calc(var(--endX) * 0.2), calc(var(--endY) * 0.2)) scale(1.2) rotate(180deg)`,
                opacity: 0.6,
              },
              "40%": {
                opacity: 0.5,
              },
              "100%": {
                transform: `translate(var(--endX), var(--endY)) scale(0.5) rotate(720deg)`,
                opacity: 0,
              },
            },
          }}
          style={{
            '--endX': `${particle.endX}px`,
            '--endY': `${particle.endY}px`,
          }}
        >
          {particle.shape}
        </Box>
      ))}

    </Box>
  );
};

// Firework Spark Component
const FireworkSpark = ({ x, y, delay, color }) => (
  <Box
    sx={{
      position: "absolute",
      top: `${y}%`,
      left: `${x}%`,
      width: "4px",
      height: "4px",
      borderRadius: "50%",
      backgroundColor: color,
      boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
      animation: `sparkle 2s ease-out ${delay}s infinite`,
      "@keyframes sparkle": {
        "0%": {
          transform: "scale(0)",
          opacity: 1,
        },
        "50%": {
          transform: "scale(1)",
          opacity: 1,
        },
        "100%": {
          transform: "scale(0)",
          opacity: 0,
        },
      },
    }}
  />
);

// Glowing Year Balloon/Bubble
const GlowingYear = () => {
  const currentYear = new Date().getFullYear();

  return (
    <Fade in timeout={2000}>
      <Box
        sx={{
          position: "absolute",
          top: { xs: -20, sm: -30, md: -40 },
          right: { xs: -20, sm: -30, md: -40 },
          width: { xs: 140, sm: 170, md: 200 },
          height: { xs: 140, sm: 170, md: 200 },
          borderRadius: "50%",
          background: "linear-gradient(135deg, rgba(255, 215, 0, 0.25) 0%, rgba(255, 105, 180, 0.25) 100%)",
          backdropFilter: "blur(10px)",
          border: "3px solid rgba(255, 215, 0, 0.6)",
          boxShadow: "0 0 30px rgba(255, 215, 0, 0.5), inset 0 0 20px rgba(255, 215, 0, 0.2)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          pointerEvents: "none",
          animation: "float 3s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translateY(0px)",
            },
            "50%": {
              transform: "translateY(-10px)",
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "32px", sm: "40px", md: "48px" },
            fontWeight: 800,
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF1493 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: "0 0 20px rgba(255, 215, 0, 0.5)",
            animation: "glow 2s ease-in-out infinite alternate",
            fontFamily: "'DM Sans', sans-serif",
            "@keyframes glow": {
              "0%": {
                filter: "brightness(1) drop-shadow(0 0 10px rgba(255, 215, 0, 0.8))",
              },
              "100%": {
                filter: "brightness(1.3) drop-shadow(0 0 15px rgba(255, 215, 0, 1))",
              },
            },
          }}
        >
          {currentYear}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "10px", sm: "12px", md: "14px" },
            fontWeight: 600,
            color: "#FFD700",
            textAlign: "center",
            mt: -0.5,
            textShadow: "0 0 10px rgba(255, 215, 0, 0.8)",
            animation: "pulse 1.5s ease-in-out infinite",
            fontFamily: "'DM Sans', sans-serif",
            "@keyframes pulse": {
              "0%, 100%": {
                opacity: 0.8,
              },
              "50%": {
                opacity: 1,
              },
            },
          }}
        >
          Happy New Year!
        </Typography>
      </Box>
    </Fade>
  );
};

const generateCrackers = () => {
  return Array.from({ length: 1 }, (_, i) => ({
    id: `cracker-${Date.now()}-${i}`,
    x: 10 + Math.random() * 80,
    y: 10 + Math.random() * 50,
    delay: 0,
    duration: 3 + Math.random() * 1, // 3-4 seconds duration
  }));
};

const NewYearMagic = () => {
  const [crackers, setCrackers] = useState([]);

  useEffect(() => {
    // Generate initial single cracker burst
    setCrackers(generateCrackers());
  }, []);

  const handleCrackerAnimationEnd = (crackerId) => {
    // Clear crackers first to force unmount
    setCrackers([]);

    // Wait -1 to 3 seconds before next burst
    const respawnDelay = -1000 + Math.random() * 4000; // -1 to 3 seconds delay

    setTimeout(() => {
      // Create new cracker at completely random position
      const newCracker = {
        id: `cracker-${Date.now()}-${Math.random()}`,
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 50,
        delay: 0,
        duration: 3 + Math.random() * 1,
      };
      console.log('New cracker spawning at:', newCracker.x, newCracker.y);
      setCrackers([newCracker]);
    }, Math.max(0, respawnDelay)); // Ensure no negative timeout
  };

  return (
    <>
      {/* Cracker Bursts */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 9996,
        }}
      >
        {crackers.map((cracker) => (
          <CrackerBurst
            key={cracker.id}
            x={cracker.x}
            y={cracker.y}
            delay={cracker.delay}
            duration={cracker.duration}
            onAnimationEnd={() => handleCrackerAnimationEnd(cracker.id)}
          />
        ))}
      </Box>

      {/* Glowing Year Text */}
      <GlowingYear />
    </>
  );
};

export default NewYearMagic;
